import { type AiTagId, type CallhomeAiTag } from '@solinkcorp/api-types';
import { type Immutable } from 'immer';
import React, { useMemo } from 'react';
import { useControllerState } from 'solox';
import { type Controller } from '../../Controller';
import { type ServiceDisplayEventsController } from '../../ServiceDisplayEventsController';
import {
  VISION_ANALYTICS_CONFIGURATION,
  type VisionAnalyticsConfig,
} from '../../hooks/useSiteSpecificVisionAnalyticsCamera';
import { type ServiceDisplayWidget } from '../../layout/types';
import { type CommonWidgetProps } from '../../types';
import CountWidget from './CountWidget';
import JourneyWidget from './JourneyWidget';
import LiveFeedWidget from './LiveFeed/LiveFeedWidget';
import TimeWidget from './TimeWidget';
import WidgetCard from './WidgetCard';

interface WidgetProps {
  controller: Controller;
  eventsController: ServiceDisplayEventsController;
  allTags: readonly CallhomeAiTag[];
  widget: Immutable<ServiceDisplayWidget>;
  aiTagConfigurationMap: Record<AiTagId, VisionAnalyticsConfig>;
  /** used for 'skeleton like' loading effect */
  isDataLoading?: boolean;
  isDragging?: boolean;
}

/** Renders a widget in a card. */
const Widget: React.FC<WidgetProps> = ({
  controller,
  eventsController,
  allTags,
  widget,
  aiTagConfigurationMap,
  isDataLoading,
  isDragging,
}) => {
  const tags = useMemo(() => tagIdsToTags(allTags, widget.tagIds), [allTags, widget.tagIds]);

  const editing = useControllerState(controller.state, (state) => !!state.editingLayout);

  const hasConnectedTags = widget.tagIds.every((tagId: AiTagId) =>
    Object.keys(aiTagConfigurationMap).includes(tagId),
  );

  const hasMultipleAssignments = widget.tagIds.some(
    (tagId: AiTagId) => (aiTagConfigurationMap[tagId]?.zoneOrLineIds.length ?? 0) > 1,
  );

  const hasZoneAndLine = widget.tagIds.some((tagId: AiTagId) => {
    const config = aiTagConfigurationMap[tagId];
    return config?.configurationType === VISION_ANALYTICS_CONFIGURATION.BOTH;
  });

  const aiTagName = useMemo(() => {
    return tags.find((tag) => tag.id === widget.tagIds[0])?.name;
  }, [widget.tagIds, tags]);

  const hasLine = widget.tagIds.some((tagId: AiTagId) => {
    const config = aiTagConfigurationMap[tagId];
    return config?.configurationType === VISION_ANALYTICS_CONFIGURATION.LINE;
  });

  const common: CommonWidgetProps = {
    controller,
    eventsController,
    widget,
    tags,
    editing,
    hasConnectedTags,
    hasMultipleAssignments,
    hasLine,
    hasZoneAndLine,
    aiTagName,
    isDataLoading,
    isDragging,
  };

  switch (widget.config.type) {
    case 'COUNT':
    case 'DRIVE_OFF':
      return <CountWidget {...common} config={widget.config} tagIds={widget.tagIds as AiTagId[]} />;
    case 'TIME':
      return <TimeWidget {...common} config={widget.config} tagIds={widget.tagIds as AiTagId[]} />;
    case 'LIVE_FEED':
      return <LiveFeedWidget {...common} config={widget.config} />;
    case 'TOTAL_JOURNEY_TIME':
      return (
        <JourneyWidget {...common} config={widget.config} tagIds={widget.tagIds as AiTagId[]} />
      );
    default:
      return (
        <WidgetCard {...common} level="normal" title={widget.title}>
          Unknown type: {(widget.config as { type: string }).type}
        </WidgetCard>
      );
  }
};

export default React.memo(Widget);

function tagIdsToTags(
  allTags: readonly CallhomeAiTag[],
  tagIds: readonly string[],
): CallhomeAiTag[] {
  return tagIds
    .flatMap((id) => {
      const tag = allTags.find((tag) => tag.id === id);
      return tag ? [tag] : [];
    })
    .filter((tag): tag is CallhomeAiTag => !!tag);
}
